import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  FaceLandmarker,
  FilesetResolver,
  DrawingUtils,
} from "@mediapipe/tasks-vision";

function FaceDetection({
  isModalOpen,
  toggleModal,
  modalStateRef,
  handleFinish,
  videoRef,
  onCameraReady,
}) {
  // State variables for dynamic data
  const [numFaces, setNumFaces] = useState(0);
  const [headPoseInfo, setHeadPoseInfo] = useState([]);
  const [statusMessage, setStatusMessage] = useState(
    "Initializing webcam and model...",
  );

  // Refs for accessing DOM elements and persistent variables
  // const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const faceLandmarkerRef = useRef(null);
  const drawingUtilsRef = useRef(null);
  const lastVideoTimeRef = useRef(-1);
  const lastWarningTimeRef = useRef(0);
  const WARNING_COOLDOWN = 6000;
  let results = null;

  // Tracks a scheduled "close the modal" timeout once a face has been
  // reacquired, so the user gets a few seconds to actually read the
  // warning instead of it vanishing the instant their face reappears.
  const closeTimeoutRef = useRef(null);
  const FACE_REACQUIRED_READ_DELAY = 3000;

  // MediaPipe related variables
  const runningMode = "VIDEO";

  // Head pose and eye gaze detection thresholds
  const CHEATING_THRESHOLD_YAW = 25; // in degrees
  const CHEATING_THRESHOLD_PITCH = 20; // in degrees
  const CHEATING_THRESHOLD_ROLL = 15; // in degrees
  const CHEATING_THRESHOLD_EYE_GAZE = 0.5; // normalized ratio (0.0 to 1.0)

  // Add these state variables at the top of the component
  const timeoutRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isMountedRef = useRef(true);

  // The main detection loop
  const predictWebcam = useCallback(() => {
    if (!isMountedRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;

    const faceLandmarker = faceLandmarkerRef.current;
    let nowInMs = performance.now();

    // Check for face landmarker instance
    if (!faceLandmarker || !video || !canvas) {
      console.warn("FaceLandmarker or video/canvas elements 45678.");
      animationFrameRef.current = requestAnimationFrame(predictWebcam);
      return;
    }

    // Set canvas dimensions to match video
    canvas.style.height = 150 + "px";
    canvas.style.width = 250 + "px";
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Initialize DrawingUtils if it's not already
    if (!drawingUtilsRef.current) {
      drawingUtilsRef.current = new DrawingUtils(canvas.getContext("2d"));
    }

    const canvasCtx = canvas.getContext("2d");

    // Mirror the canvas to match the video feed
    canvasCtx.save();

    if (lastVideoTimeRef.current !== video.currentTime) {
      lastVideoTimeRef.current = video.currentTime;
      results = faceLandmarker.detectForVideo(video, nowInMs);
    }

    if (results?.faceLandmarks) {
      const faceCount = results.faceLandmarks.length;

      // If the face was lost again before the "grace period" close timer
      // fired, cancel that scheduled close - the warning flow should
      // continue as normal (and may re-trigger below).
      if (faceCount !== 1 && closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }

      // Case 1: No face detected
      if (faceCount === 0) {
        console.log(
          "No face detected. Triggering warning modal if not already open.",
        );
        const now = Date.now();
        if (now - lastWarningTimeRef.current >= WARNING_COOLDOWN) {
          lastWarningTimeRef.current = now;

          toggleModal("faceDetectionWarning", "noFaceWarning", true);
          if (
            modalStateRef.current.faceDetectionWarningCount.noFaceWarningCount +
              modalStateRef.current.faceDetectionWarningCount
                .multiFacesWarningCount >=
            3
          ) {
            setTimeout(() => {
              handleFinish("Proctoring Fail Empty Video");
            }, 3000);
          }
        }
      }

      // Case 2: Multiple faces detected
      else if (faceCount > 1) {
        const now = Date.now();
        if (now - lastWarningTimeRef.current >= WARNING_COOLDOWN) {
          lastWarningTimeRef.current = now;

          if (!modalStateRef.current.faceDetectionWarning) {
            toggleModal("faceDetectionWarning", null, false);
            toggleModal("faceDetectionWarning", "multiFaceWarning", true);
            if (
              modalStateRef.current.faceDetectionWarningCount
                .noFaceWarningCount +
                modalStateRef.current.faceDetectionWarningCount
                  .multiFacesWarningCount >=
              3
            ) {
              setTimeout(() => {
                handleFinish("Proctoring Fail Many");
              }, 3000);
            }
          }
        }
      }

      // ✅ Case 3: Exactly one face → close modal (ignore subtype when closing)
      else {
        if (
          modalStateRef.current.faceDetectionWarning &&
          modalStateRef.current.faceDetectionWarningCount.noFaceWarningCount +
            modalStateRef.current.faceDetectionWarningCount
              .multiFacesWarningCount <=
            3
        ) {
          console.log("Face reacquired: closing modal");
          if (!closeTimeoutRef.current) {
            toggleModal("faceReacquired", null, true);
            console.log(
              "Face reacquired: pausing countdown, closing modal in " +
                FACE_REACQUIRED_READ_DELAY / 1000 +
                "s",
            );

            closeTimeoutRef.current = setTimeout(() => {
              toggleModal("faceDetectionWarning", null, false);
              closeTimeoutRef.current = null;
            }, FACE_REACQUIRED_READ_DELAY);
          }
        }
      }
    }
    // Clear the canvas and draw new landmarks
    canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
    // Draw the video frame on the canvas

    canvasCtx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    // In the predictWebcam function, replace the setTimeout logic with this:

    // Check if any faces are detected
    // if (results && results.faceLandmarks) {
    //   setNumFaces(results.faceLandmarks.length);
    //   const newHeadPoseInfo = [];

    //   for (let i = 0; i < results.faceLandmarks.length; i++) {
    //     const landmarks = results.faceLandmarks[i];

    //     // Draw the face mesh
    //     drawingUtilsRef.current.drawConnectors(
    //       landmarks,
    //       FaceLandmarker.FACE_LANDMARKS_TESSELATION,
    //       { color: "#C0C0C070", lineWidth: 1 }
    //     );

    //     // Draw face contours, eyes, and irises
    //     drawingUtilsRef.current.drawConnectors(
    //       landmarks,
    //       FaceLandmarker.FACE_LANDMARKS_FACE_OVAL,
    //       { color: "#E0E0E0", lineWidth: 5 }
    //     );
    //     drawingUtilsRef.current.drawConnectors(
    //       landmarks,
    //       FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE,
    //       { color: "#30FF30", lineWidth: 2 }
    //     );
    //     drawingUtilsRef.current.drawConnectors(
    //       landmarks,
    //       FaceLandmarker.FACE_LANDMARKS_LEFT_EYE,
    //       { color: "#30FF30", lineWidth: 2 }
    //     );
    //     drawingUtilsRef.current.drawConnectors(
    //       landmarks,
    //       FaceLandmarker.FACE_LANDMARKS_RIGHT_IRIS,
    //       { color: "#30FF30", lineWidth: 2 }
    //     );
    //     drawingUtilsRef.current.drawConnectors(
    //       landmarks,
    //       FaceLandmarker.FACE_LANDMARKS_LEFT_IRIS,
    //       { color: "#30FF30", lineWidth: 2 }
    //     );
    //     drawingUtilsRef.current.drawConnectors(
    //       landmarks,
    //       FaceLandmarker.FACE_LANDMARKS_LIPS,
    //       { color: "#30FF30", lineWidth: 2 }
    //     );

    //     // Get head pose and eye gaze info
    //     const transformationMatrix = results.facialTransformationMatrixes[i];
    //     if (transformationMatrix) {
    //       const headPose = getHeadPose(transformationMatrix);
    //       const eyeGaze = getEyeGaze(landmarks);

    //       newHeadPoseInfo.push({
    //         id: i,
    //         yaw: headPose.yaw.toFixed(2),
    //         pitch: headPose.pitch.toFixed(2),
    //         roll: headPose.roll.toFixed(2),
    //         gaze: eyeGaze.toFixed(2),
    //       });

    //       // Combined cheating detection logic
    //       //   if (
    //       //     Math.abs(headPose.yaw) > CHEATING_THRESHOLD_YAW ||
    //       //     Math.abs(headPose.pitch) > CHEATING_THRESHOLD_PITCH ||
    //       //     Math.abs(headPose.roll) > CHEATING_THRESHOLD_ROLL ||
    //       //     Math.abs(eyeGaze) > CHEATING_THRESHOLD_EYE_GAZE
    //       //   ) {
    //       //     console.log(
    //       //       `CHEATING DETECTED for Face ${
    //       //         i + 1
    //       //       }: Head Yaw=${headPose.yaw.toFixed(
    //       //         2
    //       //       )}, Eye Gaze=${eyeGaze.toFixed(2)}`
    //       //     );
    //       //   }
    //     }
    //   }
    //   setHeadPoseInfo(newHeadPoseInfo);
    // } else {
    //   setNumFaces(0);
    //   setHeadPoseInfo([]);
    // }

    canvasCtx.restore();
    window.requestAnimationFrame(predictWebcam);
  }, [
    CHEATING_THRESHOLD_EYE_GAZE,
    CHEATING_THRESHOLD_PITCH,
    CHEATING_THRESHOLD_ROLL,
    CHEATING_THRESHOLD_YAW,
    // The following setters from useState are stable and do not need to be in the dependency array
    // setNumFaces,
    // setHeadPoseInfo
  ]);

  // Function to enable the webcam stream
  // const enableCam = useCallback(() => {
  //   if (!navigator.mediaDevices?.getUserMedia) {
  //     console.warn("getUserMedia() is not supported by your browser");
  //     setStatusMessage("Error: Webcam not supported.");
  //     return;
  //   }

  //   navigator.mediaDevices
  //     .getUserMedia({ video: true })
  //     .then((stream) => {
  //       if (videoRef.current) {
  //         videoRef.current.srcObject = stream;
  //         videoRef.current.addEventListener("loadeddata", predictWebcam);
  //         setStatusMessage("Webcam active. Looking for faces...");
  //       }
  //     })
  //     .catch((err) => {
  //       console.error("Error accessing webcam:", err);
  //       setStatusMessage("Error: Could not access webcam.");
  //     });
  // }, [predictWebcam, videoRef]);

  // Function to extract head pose from the transformation matrix
  const getHeadPose = (matrix) => {
    const R = [
      [matrix.data[0], matrix.data[1], matrix.data[2]],
      [matrix.data[4], matrix.data[5], matrix.data[6]],
      [matrix.data[8], matrix.data[9], matrix.data[10]],
    ];
    const sy = Math.sqrt(R[0][0] * R[0][0] + R[1][0] * R[1][0]);
    let yaw, pitch, roll;
    if (sy < 1e-6) {
      roll = Math.atan2(-R[1][2], R[1][1]);
      pitch = Math.atan2(-R[2][0], sy);
      yaw = 0;
    } else {
      roll = Math.atan2(R[2][1], R[2][2]);
      pitch = Math.atan2(-R[2][0], sy);
      yaw = Math.atan2(R[1][0], R[0][0]);
    }
    const toDegrees = (rad) => (rad * 180) / Math.PI;
    return {
      yaw: toDegrees(yaw),
      pitch: toDegrees(pitch),
      roll: toDegrees(roll),
    };
  };

  // Function to calculate horizontal eye gaze
  const getEyeGaze = (landmarks) => {
    const rightIris =
      landmarks[FaceLandmarker.FACE_LANDMARKS_RIGHT_IRIS[0].start];
    const leftIris =
      landmarks[FaceLandmarker.FACE_LANDMARKS_LEFT_IRIS[0].start];
    const rightEyeOuter =
      landmarks[FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE[0].end];
    const rightEyeInner =
      landmarks[FaceLandmarker.FACE_LANDMARKS_RIGHT_EYE[1].start];
    const leftEyeOuter =
      landmarks[FaceLandmarker.FACE_LANDMARKS_LEFT_EYE[0].end];
    const leftEyeInner =
      landmarks[FaceLandmarker.FACE_LANDMARKS_LEFT_EYE[1].start];
    const rightEyeCenterX = (rightEyeOuter.x + rightEyeInner.x) / 2;
    const leftEyeCenterX = (leftEyeOuter.x + leftEyeInner.x) / 2;
    const rightEyeWidth = Math.abs(rightEyeOuter.x - rightEyeInner.x);
    const leftEyeWidth = Math.abs(leftEyeOuter.x - leftEyeInner.x);
    const rightGaze = (rightIris.x - rightEyeCenterX) / rightEyeWidth;
    const leftGaze = (leftIris.x - leftEyeCenterX) / leftEyeWidth;
    return (rightGaze + leftGaze) / 2;
  };

  // Use effect to load the model on component mount
  useEffect(() => {
    let landmarker;
    let activeStream;
    isMountedRef.current = true;

    const createFaceLandmarker = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm",
        );

        landmarker = await FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
          },
          runningMode: runningMode,
          numFaces: 5,
          outputFacialTransformationMatrixes: true,
          outputFaceBlendshapes: true,
        });

        faceLandmarkerRef.current = landmarker;
        setStatusMessage("Model loaded. Starting webcam...");

        // enableCam rewritten to keep stream ref
        // if (navigator.mediaDevices?.getUserMedia) {
        //   videoRef.current.addEventListener("loadeddata", predictWebcam);
        //   setStatusMessage("Webcam active. Looking for faces...");
        // }
      } catch (error) {
        console.error("Failed to load FaceLandmarker model:", error);
        setStatusMessage("Error: Failed to load the model.");
      }
    };

    createFaceLandmarker();

    return () => {
      isMountedRef.current = false;
      // 1. Close FaceLandmarker
      if (landmarker) {
        landmarker.close();
        faceLandmarkerRef.current = null;
        console.log("FaceLandmarker model closed.");
      }

      // 2. Stop webcam stream properly

      // 3. Cancel predictWebcam loop
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
        console.log("Animation loop cancelled.");
      }
    };
  }, [runningMode, predictWebcam]);

  useEffect(() => {
    // This effect will run when the component mounts or when videoRef.current becomes a valid object
    if (videoRef.current) {
      const videoElement = videoRef.current;
      const handleLoadedData = () => {
        predictWebcam();
        onCameraReady(); // Call the callback from the parent
      };
      videoElement.addEventListener("loadeddata", handleLoadedData);

      return () => {
        // Cleanup: remove the event listener
        videoElement.removeEventListener("loadeddata", handleLoadedData);
      };
    }
  }, [videoRef, onCameraReady]);

  return (
    <>
      {/* <video
        ref={videoRef}
        autoPlay
        playsInline
        style={{ display: "none" }}
      ></video> */}
      <canvas
        ref={canvasRef}
        style={{
          width: "250px",
          height: "150px",
          objectFit: "cover",
          borderRadius: "15px",
        }}
      ></canvas>
    </>
  );
}

export default FaceDetection;
